import { useLayoutEffect, useRef } from 'react'
import type { ReactNode, RefObject } from 'react'

import { ExchangeIcon } from '@/components/widget/exchange-icon'
import { tradeVenueToIconKey } from '@/lib/proto/util'
import { cn } from '@/lib/utils'
import { PublicAccount } from '@/types/generated/common/account'

/** Folder-tab style account selector shared by every "switch between a
 * handful of accounts inline" spot in the app (order-preview panel, Account
 * Cards widget). Meant to sit flush — zero gap, no bottom padding — directly
 * above a bordered/backgrounded box, with zero horizontal inset so its edges
 * line up with that box's own border: the active tab's `-mb-px` then dips
 * onto the box's top border. The active tab has no fill of its own: when the row
 * sits on an `.account-tab-strip`, the tab cuts its own box out of that strip's
 * tint (see useStripHole), so it shows exactly the surface the box below shows
 * and the two read as one continuous shape. Getting the
 * overlap to paint above the box (not under it) is the *caller's*
 * responsibility — promote the tab row to its own stacking context (e.g.
 * `relative z-10`), since the box is normally a later, unpositioned sibling
 * that would otherwise paint over it. */
export function AccountTab({
  account,
  isActive,
  title,
  accent,
  onSelect,
  hideIcon,
  plainInactive,
  maxWidthClassName,
}: {
  account: PublicAccount
  isActive: boolean
  title?: string
  /** Optional trailing marker, e.g. a "this is the real account" dot — purely
   * decorative, the caller decides if/when to show it. */
  accent?: ReactNode
  onSelect: () => void
  /** Omit the per-account exchange icon — for callers that already show the
   * exchange once elsewhere (e.g. a shared label to the left of the whole
   * tab row) and would otherwise repeat it on every single tab. */
  hideIcon?: boolean
  /** Drop the inactive tab's own border/background (plain muted text only)
   * — for callers where the surrounding bar already reads as one contained
   * group and a border+bg per inactive tab would be redundant chrome. The
   * active tab's own look (white pill) is unaffected either way. */
  plainInactive?: boolean
  /** Overrides the default `max-w-32` cap — callers with more room to spare
   * (e.g. a per-exchange bar that no longer repeats the exchange name) can
   * widen the tab so account names truncate less aggressively. */
  maxWidthClassName?: string
}) {
  const ref = useRef<HTMLButtonElement>(null)
  useStripHole(ref, isActive)

  return (
    <button
      ref={ref}
      type="button"
      onClick={onSelect}
      title={title}
      tabIndex={-1}
      className={cn(
        'flex w-fit min-w-10 shrink-0 items-center justify-center gap-1.5 border border-b-0 px-1.5 py-1.25 text-xs font-medium whitespace-nowrap transition-[transform,background-color,border-color,color] duration-150 ease-linear motion-reduce:transition-none',
        maxWidthClassName ?? 'max-w-32',
        isActive
          ? 'border-border text-foreground -mb-px min-w-12 bg-transparent px-2 font-semibold'
          : cn(
              'min-w-12 translate-y-0.5 px-2',
              plainInactive
                ? 'text-foreground/40 hover:text-foreground/60 border-transparent bg-transparent hover:bg-white/10'
                : 'border-border/50 bg-foreground/5 text-foreground/40 hover:border-border hover:text-foreground/60'
            )
      )}
    >
      {!hideIcon && (
        <ExchangeIcon
          exchange={tradeVenueToIconKey(account.tradeVenue)}
          className="size-3.5 shrink-0 rounded-full"
        />
      )}
      <span className="min-w-0 truncate">{account.name}</span>
      {accent}
    </button>
  )
}

/** Publishes the active tab's box, relative to its `.account-tab-strip`, as the
 * strip's --tab-hole-* vars. Re-measured on any scroll inside the strip (the tab
 * rows scroll horizontally), whenever the strip or the tab resizes, and once the
 * tab's own inactive→active transform transition settles. */
function useStripHole(ref: RefObject<HTMLButtonElement | null>, isActive: boolean) {
  useLayoutEffect(() => {
    const tab = ref.current
    const strip = tab?.closest<HTMLElement>('.account-tab-strip')
    if (!isActive || !tab || !strip) return

    const update = () => {
      const s = strip.getBoundingClientRect()
      const t = tab.getBoundingClientRect()
      // Rects are in screen px, but the mask is laid out in the strip's own px. They
      // differ under the UI-scale `zoom` on the dashboard (or any scale transform),
      // which would shrink the hole and drift it toward the strip's top-left.
      const scale = strip.offsetWidth ? s.width / strip.offsetWidth : 1
      const px = (value: number) => `${value / scale}px`
      strip.style.setProperty('--tab-hole-x', px(t.left - s.left))
      strip.style.setProperty('--tab-hole-y', px(t.top - s.top))
      strip.style.setProperty('--tab-hole-w', px(t.width))
      strip.style.setProperty('--tab-hole-h', px(t.height))
    }
    update()

    const observer = new ResizeObserver(update)
    observer.observe(strip)
    observer.observe(tab)
    // Scroll doesn't bubble; capture catches the inner scroller's.
    strip.addEventListener('scroll', update, true)
    tab.addEventListener('transitionend', update)
    return () => {
      observer.disconnect()
      strip.removeEventListener('scroll', update, true)
      tab.removeEventListener('transitionend', update)
      for (const prop of ['x', 'y', 'w', 'h']) strip.style.removeProperty(`--tab-hole-${prop}`)
    }
  }, [ref, isActive])
}
